import {
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { Sensor } from '../../models/classes/sensor';
import { AssetsService } from '../../services/asset.service';
import { I18nService } from '../../services/i18n.service';
import { CollapseSidebarComponent } from './collapse-sidebar/collapse-sidebar';
import { RenameCardHeaderComponent } from '../card/rename-card-header/rename-card-header';
import { SpinnerComponent } from '../spinner/spinner';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { SearchFilterComponent } from '../search-filter/search-filter';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';
import { LP_UI_CONFIG } from '../../core/tokens';

@Component({
  selector: 'app-sidebar',
  imports: [
    CollapseSidebarComponent,
    RenameCardHeaderComponent,
    SpinnerComponent,
    NzIconModule,
    SearchFilterComponent,
    RouterModule,
  ],
  templateUrl: './sidebar.html',
  styleUrls: ['./sidebar.scss'],
})
export class SidebarComponent implements OnDestroy {
  asset = input<any | null>(null);
  sensorID = input<any | null>(null);
  readonly plantFilterStatus = input<any>(null);
  plant = model<any | null>(null);
  sensor = input<Sensor | null>(null);
  telemetry = input<any | null>(null);
  sensorHealthPage = input<boolean>(false);

  plantsFilter = input<boolean>(false);
  notificationsFilter = input<boolean>(false);
  title = input<string>('Navigation');
  titleicon = input<string>('navigation');
  loading = input<boolean>(false);
  showFilter = input<boolean>(false);
  showList = input<boolean>(false);

  sidebarClick = output<void>();

  internalLoading = signal(false);
  selectedAssetId = signal<number>(GLOBAL_CONSTANT.DEFAULT_INITIALIZER);
  selectedSensorId = signal<number>(GLOBAL_CONSTANT.DEFAULT_INITIALIZER);

  public all_buildings = signal<any>([]);

  private assetsService = inject(AssetsService);
  private route = inject(ActivatedRoute);
  router = inject(Router);
  public i18n = inject(I18nService);
  public readonly cfg = inject(LP_UI_CONFIG);

  private plantSubscription: any;

  Buildings = computed(() => this.all_buildings());
  Loading = computed(() => this.loading() || this.internalLoading());

  searchFilterChanged = output<void>();

  constructor() {
    this.route.paramMap.subscribe((params) => {
      const id = +params.get('id')!;
      const assetId = +params.get('assetId')!;
      const sensorId = +params.get('sensorId')!;
      this.selectedAssetId.set(id || assetId || GLOBAL_CONSTANT.DEFAULT_INITIALIZER);
      this.selectedSensorId.set(sensorId || GLOBAL_CONSTANT.DEFAULT_INITIALIZER);
    });

    effect(() => {
      if (this.asset()) {
        this.refresh();
      }
    });
  }

  onSearchFilterChanged() {
    this.searchFilterChanged.emit();
  }
  toSensorValue(assetID: any, sensorID: any, measurementTypeID: any) {
    if (sensorID && measurementTypeID) {
      this.router.navigate(['sensor-value', assetID, sensorID, measurementTypeID]);
    }
  }

  toSensor(assetID: any, sensorID: any) {
    if (sensorID) {
      this.router.navigate(['asset', assetID, sensorID]);
    }
  }

  isLocationClickable(location: any): boolean {
    return !!location.curLayer && location.curLayer !== 'a' && location.curLayer !== 'b';
  }

  navToLocation(location: any) {
    if (!this.isLocationClickable(location)) return;
    if (location.isAsset) this.router.navigate(['asset', location.objectID, -1]);
    else this.router.navigate(['plant', location.objectID]);
  }

  sortAssociatedEquipment(assoc_eq: Location[]): any {
    return assoc_eq;
  }

  isSensorHealthCrit(asset: any) {
    const health = asset?.health?.filter(
      // TODO: horrible hack, please remove!
      (telemetry: any) =>
        telemetry.measurementTypeID !== 1048 &&
        telemetry.measurementTypeID !== 1025 &&
        telemetry.measurementTypeID !== 1026
    );
    return health.some((telemetry: any) => telemetry.critical);
  }

  isSensorHealthWarning(asset: any) {
    const health = asset.health.filter(
      // TODO: horrible hack, please remove!
      (telemetry: any) =>
        telemetry.measurementTypeID !== 1048 &&
        telemetry.measurementTypeID !== 1025 &&
        telemetry.measurementTypeID !== 1026
    );
    return health.some((telemetry: any) => telemetry.warning);
  }

  isSensorHealthInacti(asset: any) {
    const health = asset.health.filter(
      // TODO: horrible hack, please remove!
      (telemetry: any) =>
        telemetry.measurementTypeID !== 1048 &&
        telemetry.measurementTypeID !== 1025 &&
        telemetry.measurementTypeID !== 1026
    );
    return health.some((telemetry: any) => telemetry.inactive);
  }

  toSensorHealth(assetID: any) {
    this.router.navigate(['sensor-health', assetID]);
  }

  refresh() {
    if (this.asset()) {
      this.internalLoading.set(true);
      const result = this.assetsService.getPlants();
      // Plants are coming empty @Harshit
      if (result) {
        this.internalLoading.set(false);
        let building: any;
        if (this.cfg.appName === 'ia') {
          building = result.plants
            .map((plant: any) => plant.children)
            .flat(1)
            .find((building: any) =>
              building.Assets.some((asset: any) => asset?.objectID === this.asset()?.objectID)
            );
        } else if (this.cfg.appName === 'em') {
          building = result.plants
            .map((_plant: any) => _plant.children)
            .flat(2)
            .find(
              (building: any) =>
                typeof building.Assets.find(
                  (asset: any) => asset.objectID === this.asset().objectID
                ) !== 'undefined'
            );
        }
        const foundPlant: any = result.plants.find(
          (b: any) => building && b.objectID === building.parentID
        );
        if (this.cfg.appName === 'ia') {
          this.plant.set(foundPlant || null);
        } else if (this.cfg.appName === 'em') {
          if (!foundPlant) {
            const all_buildings = result.plants
              .filter((_plant) => !foundPlant || _plant.objectID === foundPlant.objectID)
              .map((_plant) => _plant.children)
              .flat(2)
              .filter((building) => building.Assets.length > 0);
            this.all_buildings.set(all_buildings);
          } else {
            const all_buildings = foundPlant.children.filter(
              (building: any) => building.Assets.length > 0
            );
            this.all_buildings.set(all_buildings);
          }
        }
      }
    }
  }

  onClick() {
    this.sidebarClick.emit();
  }

  ngOnDestroy() {
    if (this.plantSubscription) this.plantSubscription.unsubscribe();
  }
}
